import MapView from '@/components/GoogleMap/MapView';
import Header from '@/components/Header'
import useDocumentTitle from '@/components/Helmet';
import { useCustomLoader } from '@/components/Loader';
import GlobalAutocomplete from '@/components/material/Autocomplete';
import SearchFilters from '@/components/SearchFilters';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import API_URL from '@/config';
import { useToast } from '@/hooks/use-toast';
import axiosInstance from '@/lib/axiosInstance'
import { formatPrice, gradientColors, statusColors, userTypeColors } from '@/lib/utils';
import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Avatar, Box, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography, useMediaQuery } from '@mui/material'
import { Eye, Pencil, Plus, Trash } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'wouter';

interface PropertyFilters {
  category?: string;
  propertyType?: string;
  minPrice?: number;
  maxPrice?: number;
  bedrooms?: number;
  bathrooms?: number;
  city?: string;
  county?: string;
  search?: string;
  sortBy?: 'price-asc' | 'price-desc' | 'date-desc' | 'date-asc';
}

const AdminProperty = () => {
  const loader = useCustomLoader();
  useDocumentTitle("Property List");
  const { toast } = useToast();
  const [location, navigate] = useLocation();
  const isMobile = useMediaQuery("(max-width: 600px)");


  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [properties, setProperties] = useState<any>({});
  const [filters, setFilters] = useState<PropertyFilters>({});
  const [openModal, setOpenModal] = useState<any>(null);
  const [isMapView, setIsMapView] = useState(false);
  const [users, setUsers] = useState<any>([]);
  const [searchUser, setSearchUser] = useState("");
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [expanded, setExpanded] = React.useState<string | false>(false);

  const handleClose = () => setOpenModal(null);

  const user = useSelector((state: any) => state.auth.user);


  const fetchProperty = useCallback(async () => {
    loader(true);
    try {
      const params = new URLSearchParams({
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== undefined && value !== "")
        ),
        limit: rowsPerPage.toString(),
        page: (page + 1).toString(),
      });
      if (selectedUser?._id) {
        params.set('owner', selectedUser._id)
      }
      else {
        params.set('userType', user.userType)
      }
      await axiosInstance.get(`/api/properties?${new URLSearchParams(params)}`)
        .then((res) => {
          setProperties(res?.data);
        })
        .catch((error) => {
          toast({
            title: "Error!",
            description: error?.response?.data?.message,
          })
        })
    } catch (error: any) {
      toast({
        title: "Error!",
        description: error?.response?.data?.message,
      })
    }
    finally {
      loader(false);
    }
  }, [page, rowsPerPage, filters, selectedUser])

  const fetchUsers = useCallback(async (search: any) => {
    loader(true);
    try {
      await axiosInstance.get('/api/admin/users', {
        params: {
          page: 1,
          limit: 100,
          search,
          userType: JSON.stringify(["agent", "seller"])
        }
      })
        .then((res) => {
          setUsers(res.data.users);
        })

    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error!",
        description: "Something went wrong",
      })
    }
    finally {
      loader(false);
    }
  }, [])

  useEffect(() => {
    fetchProperty()
    fetchUsers(searchUser)
  }, [fetchProperty, fetchUsers])


  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {

    console.log(event.target.value)
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (newFilters: PropertyFilters) => {
    setFilters(newFilters);
    setPage(0);
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/api/properties/${openModal?._id}`)
      fetchProperty();
      handleClose();
      toast({
        variant: "success",
        title: "Success!",
        description: "Property deleted successfully",
      })
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error!",
        description: error?.response?.data?.message || "Something went wrong",
      })
    }
  }
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchUsers(searchUser);
    }, 500);
    return () => clearTimeout(timeout);
  }, [searchUser]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Paper elevation={0} className="p-4">
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <h1 className="text-3xl font-bold text-gray-900">Properties</h1>
            {/* <Button onClick={() => navigate("/seller/property/add")}>
              <Plus /> Add
            </Button> */}
          </Box>
          <br />
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid size={{ sm: 3, xs: 12 }}>
              <Input placeholder="Search by title" onChange={(e) => handleFilterChange({ ...filters, search: e.target.value })} />
            </Grid>
            <Grid size={{ sm: 3, xs: 12 }}>
              <GlobalAutocomplete
                label="Select or Enter User"
                options={users}
                value={selectedUser}
                onChange={setSelectedUser}
                getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                onInputChange={setSearchUser}
                placeholder="Select or Enter User"
              />
            </Grid>
            <Grid size={{ sm: 6, xs: 12 }} sx={{ display: "flex", justifyContent: "flex-end" }}>
              <SearchFilters onFilterChange={handleFilterChange} filters={filters} setIsMapView={setIsMapView} isMapView={isMapView} />
            </Grid>
          </Grid>
          {
            isMapView && <MapView properties={properties?.properties} />
          }
          <TableContainer component={Paper}>
            {isMobile ? <Box>

              {
                properties?.properties?.map((property: any, index: any) => (
                  <Accordion
                    expanded={expanded === property?._id}
                    key={property._id}
                    onChange={() => setExpanded(expanded === property?._id ? null : property?._id)}
                    className='mb-2'
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      aria-controls="panel1-content"
                      id="panel1-header"
                      sx={{
                        "& .MuiAccordionSummary-content": {
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        },
                        backgroundColor: "#f9fafb",
                        borderRadius: 2,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar src={property?.images?.length > 0 ? `${API_URL}/getImage/image/${property?.images[0]}` : ""} alt="" />
                        <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
                          {property.title}
                        </Typography>
                      </Box>
                      <Chip
                        label={property.isActive ? "Active" : "Inactive"}
                        size="small"
                        sx={{ ml: "auto", width: 100, height: 30 }}
                        color={property.isActive ? "success" : "error"}
                      />
                    </AccordionSummary>

                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 6 }}>
                          <span >Owner Name:</span><br />
                          <span className="font-semibold break-words overflow-hidden text-ellipsis block">{property.owner?.firstName} {property.owner?.lastName}</span>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <span >Owner Email:</span><br />
                          <span className="font-semibold break-words overflow-hidden text-ellipsis block">{property.owner?.email}</span>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <span >Owner Phone:</span><br />
                          <span className="font-semibold break-words overflow-hidden text-ellipsis block">{property.owner?.phone}</span>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <span >Price:</span><br />
                          <span className="font-semibold break-words overflow-hidden text-ellipsis block">{formatPrice(property.price)}</span>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <span >Category:</span><br />
                          <span className="font-semibold">{property.category || "N/A"}</span>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <span >Address:</span><br />
                          <span className="font-semibold break-words overflow-hidden text-ellipsis block">{property.address || "N/A"}</span>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <span >City:</span><br />
                          <span className="font-semibold">
                            {property.city || "N/A"}
                          </span>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <span>State:</span><br />
                          <span className="font-semibold">{property.state || "N/A"}</span>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <span>Country:</span><br />
                          <span className="font-semibold">{property.country || "N/A"}</span>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <span>Status:</span><br />
                          <span className="font-semibold">
                            <Chip
                              label={property.status}
                              size='small'
                              sx={{ width: 100, height: 30 }}
                              color={statusColors[property.status]}
                            />
                          </span>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <span>Description:</span><br />
                          <span className="font-semibold break-words overflow-hidden text-ellipsis block">{property.description || "N/A"}</span>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))
              }
            </Box> : <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Image</TableCell>
                  <TableCell>Owner Name</TableCell>
                  <TableCell>Owner Email</TableCell>

                  <TableCell>Title</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Property Type</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {properties?.properties?.map((property: any) => (
                  <TableRow key={property._id}>
                    <TableCell>
                      {
                        property.images.length > 0 ?
                          <img src={`${API_URL}/getImage/image/${property.images[0]}`} alt={property.title} className='w-10 h-10 object-cover rounded-lg' />
                          : " No Image"
                      }
                    </TableCell>

                    <TableCell>{`${property.owner.firstName} ${property.owner.lastName}`}</TableCell>
                    <TableCell>{property.owner.email}</TableCell>

                    <TableCell>{property.title}</TableCell>
                    <TableCell>{formatPrice(property.price)}</TableCell>
                    <TableCell>{property.category}</TableCell>
                    <TableCell sx={{ textTransform: "capitalize" }}>
                      {
                        <Chip
                          label={property.status}
                          size='small'
                          sx={{ width: 100 }}
                          color={statusColors[property.status] || "warning"} // default fallback
                        />
                      }
                    </TableCell>
                    <TableCell>{property.city}</TableCell>
                    <TableCell sx={{ textTransform: "capitalize" }}>{property.propertyType}</TableCell>
                    <TableCell >
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <Tooltip title="View">
                          <Eye className='text-green-600 cursor-pointer' onClick={() => navigate(`/property/${property._id}`)} />
                        </Tooltip>
                        {/* <Tooltip title="Edit">
                          <Pencil className='text-blue-600 cursor-pointer' onClick={() => navigate(`/seller/property/edit/${property._id}`)} />
                        </Tooltip> */}
                        {/* <Tooltip title="Delete">
                          <Trash className='text-red-600 cursor-pointer' onClick={() => setOpenModal(property)} />
                        </Tooltip> */}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>}
            <TablePagination
              component="div"
              count={properties?.total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </Paper>

        <Dialog
          open={!!openModal}
          // slots={{
          //     transition: Transition,
          // }}
          keepMounted
          onClose={handleClose}
          aria-describedby="alert-dialog-slide-description"
        >
          <DialogTitle>{`Are you sure want to delete ${openModal?.title} property?`}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-slide-description">
              This action cannot be undone
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ display: "flex", justifyContent: "space-between" }}>
            <Button onClick={handleClose} variant='error' color='primary'>Cancel</Button>
            <Button onClick={handleDelete} variant='default' color='error'>Yes, Delete It</Button>
          </DialogActions>
        </Dialog>

      </section>
    </div>
  )
}

export default AdminProperty;